import clsx from "clsx";

import type { CurvePhase, LogisticsMaintenance, MaintenancePhase, MaterielFamily, UnitCapops } from "../../api/types";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, t } from "../../i18n/i18n";
import {
  capopsSentence,
  colorTone,
  CURVE_PHASES,
  dtoColor,
  familyLabel,
  limitText,
  maintenanceColor,
  percentText,
  REQUIRED_PHASES,
} from "../../maintenance";
import { CapopsBadge, DTO_STATUS, DtoStatusIcon, TONE_TEXT } from "./DtoCurve";

/** Tableaux « tous les temps d'un coup d'œil » de la vue CAPOPS / DTO (demande d'Alexis du 04/11/2026 : « l'utilisateur
 *  doit pouvoir les voir pour tous les T ») : une ligne par unité (CAPOPS de T1 à T3, pastilles aux couleurs de
 *  l'iPad) ou par parc (DTO de T0 à T3, chaque valeur colorée face à la DTO requise du parc). La colonne du temps
 *  affiché sur la carte est mise en valeur par une bande `canvas` continue, posée derrière les lignes ; la ligne
 *  entière est un bouton (fiche de l'unité, choix du parc). Rien n'est recalculé : lecture de `Logistics.maintenance`. */

/** Colonnes : nom, puis un temps par colonne, de T0 (départ) à T3, pour la CAPOPS comme pour la DTO. */
const CAPOPS_COLUMNS = "grid grid-cols-[minmax(0,1fr)_repeat(4,3.25rem)] gap-x-1";
const DTO_COLUMNS = "grid grid-cols-[minmax(0,1fr)_repeat(4,3.25rem)] gap-x-1";

/** Bande du temps affiché, derrière l'en-tête et les lignes (colonne `index` des temps, la première étant le nom). */
export function PhaseBand({ columns, index }: { columns: string; index: number }) {
  if (index < 0) return null;
  return (
    <div aria-hidden className={clsx(columns, "pointer-events-none absolute inset-0 grid-rows-1 px-2")}>
      <span className="rounded-md bg-canvas" style={{ gridColumn: index + 2, gridRow: 1 }} />
    </div>
  );
}

/** En-tête : « Unité » / « Parc », puis les temps ; celui de la carte en gras. */
export function HeadRow({ columns, label, phases, phase }: { columns: string; label: string; phases: readonly string[]; phase: string }) {
  return (
    <div aria-hidden className={clsx(columns, "relative min-h-6 items-center px-2 text-caps text-ink-muted")}>
      <span>{label}</span>
      {phases.map((p) => (
        <span key={p} className={clsx("text-center tabular-nums", p === phase && "font-semibold text-ink")}>
          {p}
        </span>
      ))}
    </div>
  );
}

// --- CAPOPS des unités --------------------------------------------------------------------------------------------

export type CapopsRow = { key: string; name: string; phases: Partial<Record<CurvePhase, UnitCapops>> };

/** CAPOPS la plus basse d'une unité de T1 à T3 (ordre du tableau : les plus affaiblies d'abord). */
const lowestOf = (row: CapopsRow) => Math.min(...REQUIRED_PHASES.map((p) => row.phases[p]?.capops ?? Infinity));

/** Lignes du tableau : chaque unité amie estimée, nommée par la logistique du temps affiché, sinon par le dispositif
 *  (une unité absente du temps affiché reste au tableau) ; les plus affaiblies d'abord, puis au temps affiché, puis
 *  par nom. */
export function capopsRows(m: LogisticsMaintenance, names: Map<string, string>, phase: MaintenancePhase): CapopsRow[] {
  return Object.entries(m.units)
    .flatMap(([key, phases]) => (names.has(key) ? [{ key, name: names.get(key)!, phases }] : []))
    .sort(
      (a, b) =>
        lowestOf(a) - lowestOf(b) ||
        (a.phases[phase]?.capops ?? Infinity) - (b.phases[phase]?.capops ?? Infinity) ||
        a.name.localeCompare(b.name),
    );
}

/** Ce qui limite l'unité, seulement quand elle n'est pas verte : au temps affiché d'abord, sinon à son temps le plus
 *  bas (« T3 : limitée par la DTO des chars et chenillés ») ; null pour une unité verte de T1 à T3. */
export function limitNote(row: CapopsRow, phase: MaintenancePhase): string | null {
  const weak = (p: MaintenancePhase) => {
    const c = row.phases[p];
    return c && maintenanceColor(c.capops) !== "green" ? c : null;
  };
  const shown = weak(phase) ? phase : REQUIRED_PHASES.filter(weak).sort((a, b) => row.phases[a]!.capops - row.phases[b]!.capops)[0];
  if (!shown) return null;
  const limit = limitText(row.phases[shown]!);
  return limit ? t("{phase} : {limit}", { phase: shown, limit }) : null;
}

/** Tableau « CAPOPS des unités » : une ligne par unité, T0 à T3 en pastilles (T0, départ : jamais jugé dans les
 *  synthèses) ; clic : la fiche de l'unité. */
export function CapopsTable({
  rows,
  phase,
  onOpen,
}: {
  rows: CapopsRow[];
  phase: MaintenancePhase;
  onOpen?: (key: string, phases: CapopsRow["phases"]) => void;
}) {
  return (
    <div className="relative">
      <PhaseBand columns={CAPOPS_COLUMNS} index={CURVE_PHASES.indexOf(phase)} />
      <HeadRow columns={CAPOPS_COLUMNS} label={t("Unité")} phases={CURVE_PHASES} phase={phase} />
      <ul className="relative">
        {rows.map((row) => {
          const note = limitNote(row, phase);
          const name = unitDesignation(row.name);
          const summary = CURVE_PHASES.map((p) => {
            const c = row.phases[p];
            return c ? t("{phase} : {text}", { phase: p, text: capopsSentence(c) }) : t("{phase} : non posée", { phase: p });
          }).join(t(" ; "));
          const cells = CURVE_PHASES.map((p) => {
            const c = row.phases[p];
            return (
              <span key={p} className="flex justify-center">
                {c ? (
                  <CapopsBadge percent={c.capops} title={t("{phase} : {text}", { phase: p, text: capopsSentence(c) })} />
                ) : (
                  <span className="text-meta text-ink-muted" title={t("{phase} : non posée", { phase: p })}>
                    —
                  </span>
                )}
              </span>
            );
          });
          const body = (
            <>
              <span className="min-w-0">
                <span className="block text-ui leading-snug font-semibold break-words text-ink">{name}</span>
                {note && <span className="block text-meta leading-snug break-words text-ink-muted">{note}</span>}
              </span>
              {cells}
            </>
          );
          return (
            <li key={row.key}>
              {onOpen ? (
                <button
                  type="button"
                  data-maintenance-unit={row.key}
                  onClick={() => onOpen(row.key, row.phases)}
                  title={t("Ouvrir la fiche de l'unité")}
                  aria-label={t("{unit} — {summary}", { unit: name, summary })}
                  className={clsx(CAPOPS_COLUMNS, "focus-inset min-h-8 w-full items-center rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-hover")}
                >
                  {body}
                </button>
              ) : (
                <div data-maintenance-unit={row.key} className={clsx(CAPOPS_COLUMNS, "min-h-8 items-center px-2 py-1.5")}>
                  {body}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// --- DTO par parc -------------------------------------------------------------------------------------------------

/** DTO d'un parc à un temps, colorée face à la DTO requise, avec son icône d'état (T0, DTO de départ : neutre). */
function DtoCell({ value, required, phase }: { value: number; required: number | null; phase: CurvePhase }) {
  const color = dtoColor(value, required);
  const title =
    required === null
      ? t("{phase} : DTO de départ {n} %", { phase, n: Math.round(value) })
      : t("{phase} : {n} %, requise {required} % · {status}", {
          phase,
          n: Math.round(value),
          required: Math.round(required),
          status: color ? t(DTO_STATUS[color]) : "",
        });
  return (
    <span className="flex justify-center">
      <span title={title} className={clsx("inline-flex items-center gap-0.5 text-meta font-medium tabular-nums transition-colors", required === null ? "text-ink-soft" : TONE_TEXT[colorTone(color)])}>
        {required !== null && <DtoStatusIcon percent={value} required={required} size={11} />}
        {percentText(value)}
      </span>
    </span>
  );
}

/** Tableau « DTO par parc » : une ligne par parc (nom, véhicules), T0 à T3 ; clic : parc à régler. */
export function DtoTable({
  m,
  phase,
  chosen,
  onChoose,
}: {
  m: LogisticsMaintenance;
  phase: MaintenancePhase;
  chosen: MaterielFamily | null;
  onChoose: (family: MaterielFamily) => void;
}) {
  return (
    <div className="relative">
      <PhaseBand columns={DTO_COLUMNS} index={CURVE_PHASES.indexOf(phase)} />
      <HeadRow columns={DTO_COLUMNS} label={t("Parc")} phases={CURVE_PHASES} phase={phase} />
      <ul className="relative">
        {m.families.map((f) => (
          <li key={f.family}>
            <button
              type="button"
              data-maintenance-family={f.family}
              aria-current={f.family === chosen || undefined}
              onClick={() => onChoose(f.family)}
              title={t("Régler la DTO requise de ce parc")}
              className={clsx(
                DTO_COLUMNS,
                "focus-inset min-h-8 w-full items-center rounded-lg px-2 py-1.5 text-left transition-colors",
                f.family === chosen ? "bg-selected" : "hover:bg-hover",
              )}
            >
              <span className="min-w-0">
                <span className="block text-ui leading-snug font-medium break-words text-ink">{familyLabel(f.family)}</span>
                <span className="block text-meta leading-snug text-ink-muted tabular-nums">
                  {t("{n} véhicules", { n: formatNumber(Math.round(f.vehicles)) })}
                  {f.below && <span className={TONE_TEXT[colorTone(f.color[f.below])]}> · {t("sous la requise dès {phase}", { phase: f.below })}</span>}
                </span>
              </span>
              {CURVE_PHASES.map((p) => (
                <DtoCell key={p} value={f.dto[p]} required={p === "T0" ? null : f.required[p]} phase={p} />
              ))}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
